import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Car, Loader2, Trash2, CalendarDays, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { vehicleAlertsAPI } from '../services/api';
import GarageAlertSuggestion from './GarageAlertSuggestion';
import { useT } from '../services/i18n';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

// Account « Mes alertes » block: vehicle model alerts (new compatible parts) with weekly/instant toggle
const MyVehicleAlerts = () => {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  useEffect(() => { vehicleAlertsAPI.mine().then(setData).catch(() => setData({ items: [], history: [] })); }, []);

  const setFreq = async (id, frequency) => {
    setBusy(id);
    try { await vehicleAlertsAPI.setFrequency(id, frequency); setData((d) => ({ ...d, items: d.items.map((a) => (a.id === id ? { ...a, frequency } : a)) })); toast.success(frequency === 'weekly' ? 'Résumé hebdomadaire activé' : 'Envoi immédiat activé'); } catch { toast.error('Modification impossible'); } finally { setBusy(''); }
  };
  const remove = async (id) => {
    setBusy(id);
    try { await vehicleAlertsAPI.remove(id); setData((d) => ({ ...d, items: d.items.filter((a) => a.id !== id) })); toast.success('Alerte véhicule supprimée'); } catch { toast.error('Suppression impossible'); } finally { setBusy(''); }
  };

  if (!data) return <div className="flex justify-center py-6"><Loader2 className="animate-spin text-orange-500" size={20} /></div>;
  return (
    <section className="mt-8" data-testid="my-vehicle-alerts">
      <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-1"><Car size={18} className="text-orange-500" /> {t('va.title')} <span className="text-sm font-normal text-gray-500" data-testid="my-vehicle-alerts-count">({data.items.length})</span></h3>
      <p className="text-xs text-gray-500 mb-3">{t('va.intro')}</p>
      <GarageAlertSuggestion onDone={() => vehicleAlertsAPI.mine().then(setData).catch(() => {})} />
      {!data.items.length ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-6 text-center" data-testid="my-vehicle-alerts-empty">
          <p className="text-sm text-gray-500">{t('va.empty')}</p>
          <Link to="/vehicules" className="inline-block mt-2 text-sm font-semibold text-orange-600 hover:underline">{t('va.browse')}</Link>
        </div>
      ) : (
        <ul className="space-y-3">
          {data.items.map((a) => (
            <li key={a.id} data-testid={`vehicle-alert-${a.id}`} className="bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap gap-3 items-center">
              <div className="min-w-0 flex-1">
                <Link to={`/vehicules/${a.brand_slug}/${a.model_slug}`} className="font-semibold text-gray-900 hover:text-orange-600" data-testid={`vehicle-alert-name-${a.id}`}>{a.brand} {a.model}</Link>
                {a.engine && <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 text-gray-700 px-2 py-0.5 text-[11px] font-medium" data-testid={`vehicle-alert-engine-${a.id}`}>{a.engine}</span>}
                <p className="text-xs text-gray-500 mt-0.5">{a.products_count} pièce(s) suivie(s) · créée le {fmt(a.created_at)} · {a.sent_count ? `${a.sent_count} e-mail(s), dernier ${fmt(a.last_notified_at)}` : 'aucun e-mail envoyé'}</p>
              </div>
              <div className="inline-flex rounded-full border border-gray-200 bg-gray-50 p-0.5 text-xs" data-testid={`vehicle-alert-frequency-${a.id}`}>
                <button type="button" disabled={busy === a.id} onClick={() => setFreq(a.id, 'weekly')} data-testid={`vehicle-alert-weekly-${a.id}`} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-medium transition-colors ${a.frequency === 'weekly' ? 'bg-[#1e3a5f] text-white' : 'text-gray-600 hover:text-gray-900'}`}><CalendarDays size={12} /> Hebdo</button>
                <button type="button" disabled={busy === a.id} onClick={() => setFreq(a.id, 'instant')} data-testid={`vehicle-alert-instant-${a.id}`} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-medium transition-colors ${a.frequency === 'instant' ? 'bg-orange-500 text-white' : 'text-gray-600 hover:text-gray-900'}`}><Zap size={12} /> Immédiat</button>
              </div>
              <button type="button" onClick={() => remove(a.id)} disabled={busy === a.id} data-testid={`vehicle-alert-remove-${a.id}`} aria-label="Supprimer" className="p-2 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50">{busy === a.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}</button>
            </li>
          ))}
        </ul>
      )}
      {data.history.length > 0 && (
        <div className="mt-4" data-testid="my-vehicle-alerts-history">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Derniers e-mails</p>
          <ul className="text-xs text-gray-600 space-y-1">
            {data.history.slice(0, 5).map((h, i) => <li key={i}>{fmt(h.at)} — {h.subject}{h.products ? ` (${h.products} pièce(s))` : ''}</li>)}
          </ul>
        </div>
      )}
    </section>
  );
};

export default MyVehicleAlerts;
